'use client';

import RoundedImageCard from '@/components/RoundedImageCard';
import Link from 'next/link';
import { FC } from 'react';
import { TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse } from '../../_generated';
import classNames from 'classnames';
import {
    useContentfulInspectorMode,
    useContentfulLiveUpdates,
} from '@contentful/live-preview/react';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { formatDate } from '@/utils/formatDate';
import Icon from '@/components/Icon/Icon';
import styles from './RelatedInsightsCard.module.scss';

import { getLocalizedLink } from '@/utils/getLocalizedLink';
import { getLocalizedContent } from '@/utils/getLocalizedContent';

type TRelatedInsightCardProps = {
    insight: TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse;
    readMoreText: string;
    flat: boolean;
};

const RelatedInsightCard: FC<TRelatedInsightCardProps> = ({ insight, readMoreText, flat }) => {
    const { lang } = useSettingsContext();
    const liveInsight = useContentfulLiveUpdates(insight);
    const inspectorProps = useContentfulInspectorMode({ entryId: insight.sys.id });
    const formattedDate = formatDate(
        lang,
        getLocalizedContent(liveInsight.fields.publishedDate, lang),
        'monthYear',
    );
    return (
        <RoundedImageCard
            image={
                getLocalizedContent(
                    getLocalizedContent(liveInsight.fields.heroimage, lang).fields.file,
                    lang,
                ).url
                    ? `https:${getLocalizedContent(getLocalizedContent(liveInsight.fields.heroimage, lang).fields.file, lang).url}?fm=webp`
                    : undefined
            }
            alt={
                getLocalizedContent(
                    getLocalizedContent(liveInsight.fields.heroimage, lang).fields.file,
                    lang,
                ).title ||
                getLocalizedContent(
                    getLocalizedContent(liveInsight.fields.heroimage, lang).fields.file,
                    lang,
                ).description ||
                ''
            }
            containerClassName={classNames('tw-h-full tw-flex tw-flex-col', {
                'tw-shadow-none': flat,
                'tw-border-none': flat,
            })}
            contentClassName="tw-p-5 tw-flex tw-flex-col tw-grow"
            inspectorProps={inspectorProps({ fieldId: 'heroimage' })}
        >
            <div
                className="tw-mb-4 tw-text-[#71767f]"
                {...inspectorProps({ fieldId: 'publishedDate' })}
            >
                {formattedDate}
            </div>
            <p
                className={classNames(
                    'tw-text-[#003865] tw-font-gotham-bold tw-leading-extra tw-text-base tw-mb-4 tw-grow',
                    styles['max-4-lines'],
                )}
                {...inspectorProps({ fieldId: 'title' })}
            >
                {getLocalizedContent(liveInsight.fields.title, lang)}
            </p>
            <Link
                href={getLocalizedLink(
                    `/insights/${getLocalizedContent(liveInsight.fields.slug, lang)}`,
                    lang,
                )}
                className={classNames(
                    styles['read-more'],
                    'tw-text-[#ce0058] tw-font-gotham-medium tw-uppercase tw-cursor-pointer tw-group tw-self-start',
                )}
            >
                {readMoreText}
                <Icon
                    type="angleRight"
                    className="tw-inline-block tw-fill-[#ce0058] tw-w-[20px] tw-h-[20px] tw-pb-[1px] tw-text-[20px] tw-tracking-wide tw-ml-1 group-hover:tw-translate-x-1.5 tw-transition-transform "
                />
            </Link>
        </RoundedImageCard>
    );
};

export default RelatedInsightCard;
